import { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, CalendarClock, Check, ChevronsUpDown, Loader2, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";

const RECURRENCE_TABS = [
  { id: "once", icon: Calendar, label: "Once" },
  { id: "daily", icon: CalendarClock, label: "Daily" },
  { id: "weekly", icon: Repeat, label: "Weekly" },
];

const WEEKDAYS = [
  { id: 0, label: "Mon" },
  { id: 1, label: "Tue" },
  { id: 2, label: "Wed" },
  { id: 3, label: "Thu" },
  { id: 4, label: "Fri" },
  { id: 5, label: "Sat" },
  { id: 6, label: "Sun" },
];

// Map well-known legacy IANA aliases → their canonical IDs used in our curated
// timezone list. Browsers (especially older Safari/Chrome on macOS) may report
// the legacy name, so we normalise before matching.
const TZ_ALIASES = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Europe/Kiev": "Europe/Kyiv",
  "Asia/Rangoon": "Asia/Yangon",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "Asia/Katmandu": "Asia/Kathmandu",
};

function detectBrowserTz() {
  try {
    const raw = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    return TZ_ALIASES[raw] || raw;
  } catch {
    return "UTC";
  }
}

/** When the browser zone isn't directly in the curated list, fall back to a
 * curated zone that matches the browser's *current* UTC offset (e.g. the
 * user is in `Asia/Thimphu` (+6:00) → we pick `Asia/Dhaka` from the list).
 * Uses `Intl.DateTimeFormat` with `timeZoneName: "longOffset"` to compute. */
function findCuratedByOffset(browserTz, timezones) {
  try {
    const offsetOf = (tz) =>
      new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName")?.value;
    const target = offsetOf(browserTz);
    if (!target) return null;
    for (const t of timezones) {
      if (offsetOf(t.value) === target) return t.value;
    }
  } catch {
    // ignore
  }
  return null;
}

function TimezoneCombobox({ value, onChange, timezones }) {
  const [open, setOpen] = useState(false);
  const current = timezones.find((t) => t.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid="schedule-tz-select"
          className="w-full inline-flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#0F172A] hover:bg-[#F8FAFC] focus-visible:ring-[#3B82F6]"
        >
          <span className="truncate">{current ? current.label : value || "Select timezone…"}</span>
          <ChevronsUpDown size={13} className="shrink-0 text-[#94A3B8]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]"
        data-testid="schedule-tz-popover"
      >
        <Command>
          <CommandInput
            placeholder="Search timezone…"
            data-testid="schedule-tz-search"
            className="h-9"
          />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No matching timezone</CommandEmpty>
            <CommandGroup>
              {timezones.map((t) => (
                <CommandItem
                  key={t.value}
                  value={`${t.value} ${t.label}`}
                  onSelect={() => { onChange(t.value); setOpen(false); }}
                  data-testid={`schedule-tz-option-${t.value}`}
                >
                  <Check
                    size={13}
                    className={`mr-2 ${value === t.value ? "opacity-100 text-[#3B82F6]" : "opacity-0"}`}
                  />
                  {t.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Re-usable schedule dialog. Used for both tests + suites + standalone (Test
 * Cases) page. Caller passes `entityType` ("test"|"suite") + `entityId` +
 * `environments` (must be non-empty — user picks at schedule time). */
export function ScheduleDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityName,
  environments = [],
  defaultEnvironmentId,
}) {
  const [timezones, setTimezones] = useState([]);
  const [tz, setTz] = useState(detectBrowserTz());
  const [envId, setEnvId] = useState(defaultEnvironmentId || environments[0]?.id || "");
  const [recurrence, setRecurrence] = useState("once");
  // Defaults: 1 hour from now (rounded) for one-time, 09:00 for recurring.
  const initialDate = useMemo(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0); d.setMilliseconds(0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  const [runDateLocal, setRunDateLocal] = useState(initialDate);
  const [runTimeLocal, setRunTimeLocal] = useState("09:00");
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]); // Mon–Fri default
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadSchedules = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/${entityType}s/${entityId}/schedules`);
      setSchedules(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (!open) return;
    api.get("/scheduling/timezones").then((r) => setTimezones(r.data.items || [])).catch(() => {});
    loadSchedules();
  }, [open, loadSchedules]);

  // Resolve the timezone against the curated list once it loads.
  // Priority: (1) browser TZ exact match → keep, (2) browser TZ alias-mapped,
  // (3) any curated zone with the same current UTC offset, (4) UTC fallback.
  useEffect(() => {
    if (!timezones.length) return;
    if (timezones.some((t) => t.value === tz)) return;
    const browserTz = detectBrowserTz();
    if (timezones.some((t) => t.value === browserTz)) {
      setTz(browserTz);
      return;
    }
    const offsetMatch = findCuratedByOffset(browserTz, timezones);
    setTz(offsetMatch || "UTC");
  }, [timezones, tz]);

  const toggleWeekday = (id) => {
    setWeekdays((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id].sort()));
  };

  const handleSubmit = async () => {
    if (!envId) {
      toast.error("Please pick an environment");
      return;
    }
    if (recurrence === "weekly" && !weekdays.length) {
      toast.error("Pick at least one weekday");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        environment_id: envId,
        timezone: tz,
        recurrence_type: recurrence,
        run_date_local: recurrence === "once" ? runDateLocal : null,
        run_time_local: recurrence === "once" ? null : runTimeLocal,
        weekdays: recurrence === "weekly" ? weekdays : null,
      };
      await api.post(`/${entityType}s/${entityId}/schedules`, payload);
      toast.success("Schedule created");
      loadSchedules();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.delete(`/schedules/${id}`);
      toast.success("Schedule cancelled");
      loadSchedules();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const envName = (id) => environments.find((e) => e.id === id)?.name || "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto rounded-xl border-[#E5E7EB]" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">
            Schedule {entityType === "suite" ? "suite" : "test"}
          </DialogTitle>
          <DialogDescription>
            {entityName ? <>"{entityName}" — </> : null}
            run automatically at the time you pick. DST is handled by the chosen timezone.
          </DialogDescription>
        </DialogHeader>

        {/* Recurrence tabs */}
        <div className="flex items-center gap-1 p-1 bg-[#F8FAFC] border border-[#E5E7EB] rounded-md w-fit">
          {RECURRENCE_TABS.map((r) => {
            const Icon = r.icon;
            const active = recurrence === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setRecurrence(r.id)}
                data-testid={`schedule-recurrence-${r.id}`}
                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-medium transition-colors ${
                  active ? "bg-[#0F172A] text-white" : "text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
                }`}
              >
                <Icon size={12} />
                {r.label}
              </button>
            );
          })}
        </div>

        {/* Time inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {recurrence === "once" ? (
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-sm font-medium text-[#475569]">Run at</label>
              <Input
                type="datetime-local"
                value={runDateLocal}
                onChange={(e) => setRunDateLocal(e.target.value)}
                data-testid="schedule-run-date-local"
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#475569]">At time</label>
              <Input
                type="time"
                value={runTimeLocal}
                onChange={(e) => setRunTimeLocal(e.target.value)}
                data-testid="schedule-run-time-local"
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Timezone</label>
            <TimezoneCombobox value={tz} onChange={setTz} timezones={timezones} />
          </div>
        </div>

        {recurrence === "weekly" && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-[#475569]">Weekdays</label>
            <div className="flex flex-wrap gap-1.5" data-testid="schedule-weekdays">
              {WEEKDAYS.map((d) => {
                const active = weekdays.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleWeekday(d.id)}
                    data-testid={`schedule-weekday-${d.id}`}
                    className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${
                      active
                        ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                        : "bg-white text-[#475569] border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-[#475569]">Environment</label>
          {environments.length === 0 ? (
            <p className="text-sm text-[#B45309] bg-[#FEF3C7] border border-[#FBBF24]/30 rounded-md px-3 py-2">
              No environments available. {entityType === "test" ? "Standalone tests need at least one environment in any project, or add one to its project." : "Add an environment to this project first."}
            </p>
          ) : (
            <Select value={envId} onValueChange={setEnvId}>
              <SelectTrigger className="rounded-md border-[#E5E7EB] focus:ring-[#3B82F6]" data-testid="schedule-env-select">
                <SelectValue placeholder="Pick an environment" />
              </SelectTrigger>
              <SelectContent>
                {environments.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`schedule-env-option-${e.id}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.type_color || "#64748B" }} />
                      <span>{e.name}</span>
                      <span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{e.type}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || environments.length === 0 || !envId}
            data-testid="schedule-submit"
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60"
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            Create schedule
          </button>
        </DialogFooter>

        {/* Existing schedules list */}
        <div className="border-t border-[#E5E7EB] pt-4 mt-2">
          <p className="text-xs font-medium uppercase tracking-wide text-[#64748B] mb-2">
            Active schedules ({schedules.filter((s) => s.status === "active").length})
          </p>
          {loading ? (
            <p className="text-sm text-[#64748B]">Loading…</p>
          ) : schedules.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">No schedules yet.</p>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]" data-testid="schedule-existing-list">
              {schedules.map((s) => (
                <li
                  key={s.id}
                  data-testid={`schedule-row-${s.id}`}
                  className="py-2.5 flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0F172A]">
                      <span className="font-medium capitalize">{s.recurrence_type}</span>
                      <span className="text-[#64748B]">
                        {" "}· {s.recurrence_type === "once"
                          ? s.run_date_local
                          : s.run_time_local}{" "}
                        ({s.timezone})
                      </span>
                      {s.recurrence_type === "weekly" && s.weekdays?.length > 0 && (
                        <span className="text-[#64748B]">
                          {" "}· {s.weekdays.map((d) => WEEKDAYS[d]?.label).filter(Boolean).join(", ")}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">
                      env: <span className="text-[#64748B]">{envName(s.environment_id)}</span>
                      {" · "}status: <span
                        className={`uppercase tracking-wide text-[10px] ${
                          s.status === "active" ? "text-[#0F766E]" : "text-[#94A3B8]"
                        }`}
                      >{s.status}</span>
                      {s.next_run_at_utc && (
                        <>
                          {" · "}next: <span className="text-[#475569]">{formatDateTime(s.next_run_at_utc)}</span>
                        </>
                      )}
                    </p>
                  </div>
                  {s.status === "active" && (
                    <button
                      type="button"
                      onClick={() => handleCancel(s.id)}
                      data-testid={`schedule-cancel-${s.id}`}
                      aria-label="Cancel schedule"
                      title="Cancel schedule"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#FCA5A5] hover:bg-[#FEF2F2]"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
